/**
 * Was auf einer Karte als Text stehen darf — die Regel für JEDE Karte. (15.10.2026)
 *
 * Martin, mit Bild (nach „Haus mit Schaf speichern“): *„Diese Karten endlich
 * überarbeiten? … Wie kann es zu solchen Darstellungen kommen, mit diesen
 * komischen Pfaden, Buttons und allem?“* Auf dem Bild: „Blender · Speichern“
 * mit `/Users/…/Documents/Vespera/haus.blend` als Fließtext, und darunter
 * eine Karte mit „NICHTS in Blender ausgeführt. Frag den Nutzer in einem
 * Satz: …“ — der Hinweis, den das Ja-Tor an das MODELL schreibt, als
 * Kartentext.
 *
 * Die Lücke: das Grundgerüst (lib/karte.ts) prüfte Gestalt (Klassen, kein
 * `style=`, ein Titel), aber nicht den WORTLAUT. Jedes Feld eines Werkzeugs
 * konnte unverändert zum Kartentext werden — und `hinweis` ist in 43
 * Werkzeugen der Satz an das Modell, nie an Martin.
 *
 * Darum zwei Regeln, hier an EINER Stelle, ohne DOM:
 *
 *   1. Ein Dateipfad erscheint nie roh. Sichtbar ist der Dateiname
 *      („haus.blend“) und der Ort in Worten („auf deinem Mac“); der volle
 *      Pfad steht höchstens im Tooltip (lib/karte.ts, dateiZeile).
 *   2. Ein Satz an das Modell („Frag den Nutzer …“, „Sag nicht
 *      „gespeichert““, „rufe blender_szene auf“) erscheint nie als
 *      Kartentext. Er fällt weg; bleibt nichts übrig, steht ein neutraler
 *      Satz da. Den STAND sagt die Karte aus den Feldern
 *      (`wartet_auf_bestaetigung`, `ausgefuehrt`), nicht aus dem Hinweis.
 *
 * Die Bausteine in lib/karte.ts rufen `fuerMenschen` für jeden Text, und
 * `kartenTextSaeubern` geht am Ende noch einmal über jede fertige Karte —
 * auch über die, die an den Bausteinen vorbei `textContent` setzen.
 * tests/frontend/karten-ohne-pfade.test.ts baut jede registrierte Karte
 * mit Pfaden und Anweisungssätzen und prüft den sichtbaren Text.
 */

/**
 * Ein absoluter Pfad: Mac (`/Users/…`, `/Volumes/…`, `~/…`), Server
 * (`/home/…`, `/opt/…`, `/var/…`, …) oder Windows (`C:\…`). Zeichen, die
 * einen Pfad in einem Satz beenden (Leerraum, Anführungszeichen, Klammern,
 * Komma, Semikolon), gehören nicht dazu; ein Punkt am Ende ist der Satzpunkt.
 */
const PFAD =
  /(?:~|\/(?:Users|Volumes|home|opt|var|srv|tmp|etc|private|root|mnt|usr|Applications|Library))\/[^\s"'„“”«»‚‘’()[\]{}<>,;]+|\b[A-Za-z]:\\[^\s"'„“”«»‚‘’()[\]{}<>,;]+/g;

/** Nur der Dateiname: „/Users/martin/Documents/Vespera/haus.blend“ → „haus.blend“. */
export function dateiName(pfad: string): string {
  const ohneEnde = pfad.trim().replace(/[/\\]+$/, "");
  const teile = ohneEnde.split(/[/\\]/);
  return teile[teile.length - 1] || ohneEnde;
}

/** Der Ort in Worten — oder leer, wenn der Pfad keinen verrät. */
export function dateiOrt(pfad: string): string {
  const p = pfad.trim();
  if (/^(?:~|\/Users|\/Volumes|\/Applications)(?:\/|$)/.test(p)) return "auf deinem Mac";
  if (/^[A-Za-z]:\\/.test(p)) return "auf deinem PC";
  if (/^\/(?:home|opt|var|srv|tmp|etc|private|root|mnt|usr)(?:\/|$)/.test(p)) return "auf dem Server";
  return "";
}

/** Ist das ein absoluter Pfad (und nicht nur ein Name)? */
export function istPfad(s: string): boolean {
  PFAD.lastIndex = 0;
  const t = PFAD.exec(s.trim());
  return t !== null && t.index === 0;
}

/** Jeder Pfad im Text wird zu seinem Dateinamen; ein Satzpunkt dahinter bleibt. */
export function ohnePfade(text: string): string {
  return text.replace(PFAD, (treffer) => {
    const ende = /[.:!?]+$/.exec(treffer)?.[0] ?? "";
    const pfad = ende ? treffer.slice(0, -ende.length) : treffer;
    return dateiName(pfad) + ende;
  });
}

/**
 * Sätze, die an das Modell gerichtet sind. Jedes Muster ist ein Satz, den
 * ein Werkzeug WIRKLICH schreibt (server/sitzungs-ja.ts, tools/blender*.ts,
 * tools/versand-bestaetigung.ts, tools/screen.ts) — kein Raten an „du“:
 * ein Brief an Martin (deliver_text) sagt auch „du kannst“, und der bleibt.
 */
const ANWEISUNG: readonly RegExp[] = [
  /^NICHTS in .{1,60} ausgeführt/i,
  /\bFrag(?:e)? (?:den Nutzer|ihn|Martin)\b/,
  /\bfrag(?:e)? (?:ihn |den Nutzer )?nicht noch einmal\b/i,
  /\bSag (?:dem Nutzer |ihm |Martin )?(?:NICHT|nicht|nie|genau das|das nur|ihm)\b/,
  /\bNenne (?:Martin|dem Nutzer|ihm)\b/,
  /\bin deiner Antwort\b/,
  /\bdarfst du\b/,
  /\bDas Ja (?:muss|gilt)\b/,
  /\bsein(?:em)? Ja\b/,
  /\bseine(?:r)? nächsten? Nachricht\b/,
  /\b(?:de[mnr]|des) Nutzers?\b/,
  /\bEntscheide von hier aus\b/,
  /\bsiehe ergebnisse\b/i,
  /\blies vorher nichts\b/i,
  /\bbestaetigt\s*=\s*true\b/,
  // Ein Werkzeugname (blender_szene, vorschlag_id, mac_anbindungen) ist Code,
  // kein Wort — außer er ist ein Dateiname (haus_mit_schaf.blend).
  /\b[a-z]{2,}(?:_[a-z0-9]+)+\b(?!\.[a-z0-9]{1,5}\b)/,
];

/** Ist dieser Satz an das Modell gerichtet? */
export function istAnweisung(satz: string): boolean {
  return ANWEISUNG.some((m) => m.test(satz));
}

/** Der Text in Sätzen — getrennt nach Satzzeichen (auch vor „…“ oder nach einem schließenden Anführungszeichen). */
function saetze(text: string): string[] {
  return text
    .split(/(?<=[.!?…]["“”»‘’']?)\s+(?=["„“«‚(A-ZÄÖÜ0-9])/u)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Die Sätze an das Modell fallen weg; die an Martin bleiben in ihrer
 * Reihenfolge — Zeile für Zeile, damit ein Brief seine Absätze behält.
 */
export function ohneAnweisungen(text: string): string {
  return text
    .split("\n")
    .map((zeile) =>
      saetze(zeile)
        .filter((s) => !istAnweisung(s))
        .join(" "),
    )
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Der Text, wie ein Mensch ihn sehen darf: ohne Sätze an das Modell, ohne
 * rohe Pfade. Bleibt nichts, steht `ersatz` da.
 */
export function fuerMenschen(text: string, ersatz = ""): string {
  const sauber = ohnePfade(ohneAnweisungen(text)).replace(/[ \t]{2,}/g, " ").trim();
  return sauber || ersatz;
}

/**
 * Dieselbe Regel für einen einzelnen Textknoten einer fertigen Karte —
 * behutsam: Leerraum am Rand bleibt (ein „ · “ zwischen zwei Spannen,
 * das „Rechnung “ vor der Nummer), und ein Text ohne Satz an das Modell
 * bleibt Zeichen für Zeichen, wie er war (nur Pfade werden zu Namen).
 */
export function textknotenFuerMenschen(text: string): string {
  const rand = /^\s*/.exec(text)?.[0] ?? "";
  const ende = /\s*$/.exec(text)?.[0] ?? "";
  const kern = text.slice(rand.length, text.length - ende.length);
  if (!kern) return text;
  const ohne = kern.split("\n").some((z) => saetze(z).some(istAnweisung)) ? ohneAnweisungen(kern) : kern;
  const neu = ohnePfade(ohne);
  return neu ? rand + neu + ende : "";
}

/** Die Statuszeilen, die die Karte statt des Hinweises an das Modell zeigt. */
export const STAND_WARTET = "wartet auf dein Ja";
export const STAND_NICHT_AUSGEFUEHRT = "Nicht ausgeführt – es fehlt deine Freigabe.";

/**
 * Satzanfänge des Ja-Tors (server/sitzungs-ja.ts, tools/versand-bestaetigung.ts):
 * das Werkzeug hat NICHTS getan und wartet auf Martins Ja — kein Fehler.
 * Dieselben Anfänge, die server/werkzeug-ergebnis.ts als „berechtigtes Nein“ kennt.
 */
const JA_TOR = /^(?:NICHTS in .{1,60} ausgeführt|Keine gültige Bestätigung|Das Ja muss aus einer)/;

/**
 * Wartet dieses Ergebnis auf Martins Ja? Zuerst die Felder
 * (`wartet_auf_bestaetigung`, `bestaetigung_noetig`, die Schlüssel
 * `ja-fehlt`/`bestaetigung-noetig`), dann der Satzanfang des Ja-Tors.
 * Ein bloßes `ausgefuehrt: false` reicht nicht — das sagen auch „nicht im
 * Ping“ und andere Gründe (tools/mac_rueckgaengig.ts).
 */
export function wartetAufJa(r: Record<string, unknown>): boolean {
  if (r.wartet_auf_bestaetigung === true || r.bestaetigung_noetig === true) return true;
  if (r.error === "ja-fehlt" || r.error === "bestaetigung-noetig") return true;
  for (const feld of ["error", "hinweis", "hint", "satz", "message"]) {
    const w = r[feld];
    if (typeof w === "string" && JA_TOR.test(w.trim())) return true;
  }
  return false;
}
